import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User, Mail, Building, Lock, Save, Loader2, Plus, X, Edit2, CreditCard, ArrowRight, Crown, Check } from 'lucide-react';
import { profileAPI, workspaceAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import usePermissions from '../hooks/usePermissions';
import { useToast } from '../contexts/ToastContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { formatServerDate } from '../utils/datetime';

// Accessibility controls relocated to top-level Settings (`/settings`) and
// the header quick-access popover. See `components/Accessibility/AccessibilityPanel.tsx`.

const Profile: React.FC = () => {
    const { user, setUser } = useAuth();
    const { canEditWorkspace } = usePermissions();
    const { showToast } = useToast();
    const { workspaces, activeWorkspace, setActiveWorkspace, refetch: refetchWorkspaces } = useWorkspace();
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newWorkspaceName, setNewWorkspaceName] = useState('');
    const [creatingWorkspace, setCreatingWorkspace] = useState(false);
    const [editingWsId, setEditingWsId] = useState<string | null>(null);
    const [editName, setEditName] = useState('');
    const [savingWsId, setSavingWsId] = useState<string | null>(null);

    const handleRenameWorkspace = async (wsId: string) => {
        if (!editName.trim()) return;
        setSavingWsId(wsId);
        try {
            const res = await workspaceAPI.update(wsId, { name: editName.trim() });
            const warning = res.data?.slug_warning;
            showToast(warning || 'Workspace renamed successfully', 'success');
            setEditingWsId(null);
            refetchWorkspaces();
        } catch (error: any) {
            showToast(error.response?.data?.detail || 'Failed to rename workspace', 'error');
        } finally {
            setSavingWsId(null);
        }
    };

    const handleCreateWorkspace = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newWorkspaceName.trim()) return;
        setCreatingWorkspace(true);
        try {
            await workspaceAPI.create({ name: newWorkspaceName.trim() });
            showToast('Workspace created successfully!', 'success');
            setNewWorkspaceName('');
            setShowCreateModal(false);
            refetchWorkspaces();
        } catch (error: any) {
            showToast(error.response?.data?.detail || 'Failed to create workspace', 'error');
        } finally {
            setCreatingWorkspace(false);
        }
    };

    const [formData, setFormData] = useState({
        full_name: '',
        company_name: '',
        email: '',
        current_password: '',
        new_password: '',
        confirm_password: '',
    });

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await profileAPI.getProfile();
                const data = response.data;
                setFormData({
                    full_name: data.full_name || '',
                    company_name: data.company_name || '',
                    email: data.email || '',
                    current_password: '',
                    new_password: '',
                    confirm_password: '',
                });
                // Update auth context user if needed
                if (user) {
                    setUser({
                        ...user,
                        full_name: data.full_name,
                        company_name: data.company_name,
                        email: data.email,
                    });
                }
            } catch (error) {
                console.error('Failed to fetch profile:', error);
                showToast('Failed to load profile information', 'error');
            } finally {
                setFetching(false);
            }
        };

        fetchProfile();
    }, [showToast, setUser]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (formData.new_password && formData.new_password !== formData.confirm_password) {
            showToast('New passwords do not match', 'error');
            return;
        }

        setLoading(true);
        try {
            const updateData: any = {
                full_name: formData.full_name,
                company_name: formData.company_name,
                email: formData.email,
            };

            if (formData.new_password) {
                updateData.current_password = formData.current_password;
                updateData.new_password = formData.new_password;
            }

            const response = await profileAPI.updateProfile(updateData);
            showToast('Profile updated successfully', 'success');

            // Update local storage and context if email/name changed
            if (user) {
                const updatedUser = {
                    ...user,
                    full_name: response.data.full_name,
                    company_name: response.data.company_name,
                    email: response.data.email,
                };
                setUser(updatedUser);
            }

            // Clear password fields
            setFormData(prev => ({
                ...prev,
                current_password: '',
                new_password: '',
                confirm_password: '',
            }));
        } catch (error: any) {
            console.error('Failed to update profile:', error);
            showToast(error.response?.data?.detail || 'Failed to update profile', 'error');
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto pb-8">
            <div className="grid md:grid-cols-3 gap-6 mb-4">
                {/* Profile Card */}
                <div className="md:col-span-1 space-y-6">
                    <h1 className="text-4xl font-bold gradient-text">User Profile</h1>

                    <div className="card text-center">
                        <div className="w-24 h-24 mx-auto bg-gradient-to-br from-primary to-purple-600 rounded-full flex items-center justify-center text-white text-3xl font-bold shadow-lg mb-4">
                            {formData.full_name.charAt(0).toUpperCase()}
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white truncate">
                            {formData.full_name}
                        </h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate mb-4">
                            {formData.email}
                        </p>
                        <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                            <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">
                                Member Since
                            </p>
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                {user?.created_at ? formatServerDate(user.created_at) : 'Recently'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Edit Form */}
                <div className="md:col-span-2">
                    <div className="card relative">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Edit Profile Information</h3>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center space-x-2">
                                        <User className="w-4 h-4" />
                                        <span>Full Name</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="full_name"
                                        value={formData.full_name}
                                        onChange={handleChange}
                                        className="input-field"
                                        placeholder="Enter full name"
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center space-x-2">
                                        <Mail className="w-4 h-4" />
                                        <span>Email Address</span>
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="input-field"
                                        placeholder="Enter email"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center space-x-2">
                                    <Building className="w-4 h-4" />
                                    <span>Company Name</span>
                                </label>
                                <input
                                    type="text"
                                    name="company_name"
                                    value={formData.company_name}
                                    onChange={handleChange}
                                    className="input-field"
                                    placeholder="Enter company name"
                                />
                            </div>

                            <div className="pt-6 border-t border-gray-100 dark:border-gray-700">
                                <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Change Password</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                    Leave these fields blank if you don't want to change your password.
                                </p>

                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center space-x-2">
                                            <Lock className="w-4 h-4" />
                                            <span>Current Password</span>
                                        </label>
                                        <input
                                            type="password"
                                            name="current_password"
                                            value={formData.current_password}
                                            onChange={handleChange}
                                            className="input-field"
                                            placeholder="Required to change password"
                                            required={!!formData.new_password}
                                        />
                                    </div>

                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">New Password</label>
                                            <input
                                                type="password"
                                                name="new_password"
                                                value={formData.new_password}
                                                onChange={handleChange}
                                                className="input-field"
                                                placeholder="Min 8 characters"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Confirm New Password</label>
                                            <input
                                                type="password"
                                                name="confirm_password"
                                                value={formData.confirm_password}
                                                onChange={handleChange}
                                                className="input-field"
                                                placeholder="Repeat new password"
                                                required={!!formData.new_password}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-4">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="btn-primary w-full flex items-center justify-center space-x-2"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                            <span>Saving Changes...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-5 h-5" />
                                            <span>Save Profile Changes</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Workspace Switcher Section */}
            <div className="card mb-8">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                        <Building className="w-6 h-6 text-primary" />
                        <h2 className="text-2xl font-bold gradient-text">Workspaces</h2>
                    </div>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-primary to-purple-600 text-white rounded-xl font-medium hover:shadow-lg hover:scale-105 transition-all text-sm"
                    >
                        <Plus className="w-4 h-4" />
                        <span>Create Workspace</span>
                    </button>
                </div>

                {workspaces.length === 0 ? (
                    <p className="text-gray-500 dark:text-gray-400 text-center py-6">
                        No workspaces yet. Create one to get started.
                    </p>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {workspaces.map(ws => {
                            const isEditing = editingWsId === ws.id;
                            // 🚨 Loops over EVERY workspace, not the active one, so this is
                            // the one check that cannot move to per-workspace roles — see
                            // usePermissions.canEditWorkspace for why.
                            const canEdit = canEditWorkspace(ws);

                            return (
                                <div
                                    key={ws.id}
                                    className={`relative p-4 rounded-xl border-2 transition-all ${
                                        activeWorkspace?.id === ws.id
                                            ? 'border-primary bg-blue-50 dark:bg-blue-900/20'
                                            : 'border-gray-200 dark:border-gray-700 hover:border-primary/50'
                                    } ${!isEditing ? 'cursor-pointer' : ''}`}
                                    onClick={!isEditing ? () => setActiveWorkspace(ws) : undefined}
                                >
                                    {isEditing ? (
                                        <form
                                            onSubmit={(e) => { e.preventDefault(); handleRenameWorkspace(ws.id); }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <input
                                                type="text"
                                                value={editName}
                                                onChange={(e) => setEditName(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Escape' && setEditingWsId(null)}
                                                className="w-full mb-2 px-2 py-1 text-sm font-semibold rounded-lg border border-primary/50 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                                autoFocus
                                                required
                                            />
                                            <div className="flex space-x-2">
                                                <button
                                                    type="submit"
                                                    disabled={savingWsId === ws.id}
                                                    className="flex-1 px-2 py-1 text-xs rounded-lg bg-gradient-to-r from-primary to-purple-600 text-white font-medium disabled:opacity-50"
                                                >
                                                    {savingWsId === ws.id ? 'Saving...' : 'Save'}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); setEditingWsId(null); }}
                                                    className="flex-1 px-2 py-1 text-xs rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </form>
                                    ) : (
                                        <>
                                            {activeWorkspace?.id === ws.id && (
                                                <div className="absolute top-3 right-3">
                                                    <Check className="w-5 h-5 text-primary" />
                                                </div>
                                            )}
                                            <div className="flex items-center space-x-2 mb-1 pr-6">
                                                <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                                                    {ws.name}
                                                </h3>
                                                {ws.owner_id === user?.id && (
                                                    <Crown className="w-4 h-4 text-amber-500 flex-shrink-0" />
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                Created {formatServerDate(ws.created_at)}
                                            </p>
                                            {canEdit && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEditingWsId(ws.id);
                                                        setEditName(ws.name);
                                                    }}
                                                    className="absolute bottom-3 right-3 p-1 text-gray-400 hover:text-primary dark:hover:text-primary transition-colors rounded"
                                                    title="Rename workspace"
                                                >
                                                    <Edit2 className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Create Workspace Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="glass rounded-2xl shadow-2xl w-full max-w-sm p-6 animate-fade-in">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Create Workspace</h2>
                            <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleCreateWorkspace} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Workspace Name</label>
                                <input
                                    type="text"
                                    value={newWorkspaceName}
                                    onChange={(e) => setNewWorkspaceName(e.target.value)}
                                    className="input-field"
                                    placeholder="My Workspace"
                                    required
                                    autoFocus
                                />
                            </div>
                            <div className="flex space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setShowCreateModal(false)}
                                    className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={creatingWorkspace}
                                    className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-primary to-purple-600 text-white font-medium hover:shadow-lg hover:scale-105 transition-all disabled:opacity-50 disabled:scale-100"
                                >
                                    {creatingWorkspace ? 'Creating...' : 'Create'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Billing shortcut card */}
            <div className="card mb-8">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4 min-w-0">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center flex-shrink-0">
                            <CreditCard className="w-6 h-6 text-white" />
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Billing & Usage</h2>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                View your plan, track usage, change tier, and manage invoices.
                            </p>
                        </div>
                    </div>
                    <Link
                        to="/billing"
                        className="btn-primary flex items-center gap-2 flex-shrink-0 whitespace-nowrap"
                    >
                        Open Billing
                        <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>

        </div>
    );
};

export default Profile;
